import React, { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CalendarRange, Loader2, Plus, Sprout } from "lucide-react";
import { toast } from "sonner";

const PRODUCER = ["farmer", "cooperative", "collective_executive", "admin"];

const ScheduleForm = ({ coops, onCreated }) => {
  const [f, setF] = useState({ coop_id: coops[0]?.id || "", product: "Wheat", season: "2026-27", planned_kg: 20000, sowing_date: "2026-10-15",
    harvest_start: "2027-06-20", harvest_end: "2027-07-10", method: "regenerative", water_source: "drip" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/commons/schedules", { ...f, planned_kg: +f.planned_kg }); toast.success("Production schedule anchored"); onCreated(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const cls = "bg-[#050A15] border border-white/10 px-2 py-1.5 text-xs font-mono outline-none focus:border-[#00FF7F] w-full";
  return (
    <form onSubmit={submit} className="qs-panel p-4 grid md:grid-cols-4 xl:grid-cols-8 gap-2 items-end" data-testid="schedule-form">
      <select data-testid="schedule-coop" className={cls} value={f.coop_id} onChange={upd("coop_id")}>{coops.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
      <input data-testid="schedule-product" className={cls} value={f.product} onChange={upd("product")} />
      <input className={cls} value={f.season} onChange={upd("season")} />
      <input data-testid="schedule-kg" className={cls} type="number" value={f.planned_kg} onChange={upd("planned_kg")} />
      <input className={cls} type="date" value={f.sowing_date} onChange={upd("sowing_date")} />
      <input className={cls} type="date" value={f.harvest_start} onChange={upd("harvest_start")} />
      <select className={cls} value={f.method} onChange={upd("method")}>{["regenerative", "organic", "conventional"].map((m) => <option key={m}>{m}</option>)}</select>
      <button data-testid="schedule-submit" disabled={busy} className="qs-cta bg-[#00FF7F] text-[#050A15] font-semibold px-3 py-1.5 text-xs flex items-center justify-center gap-1">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5" /> Plan</>}
      </button>
    </form>
  );
};

export const SchedulesPanel = ({ schedules, coops, onChange }) => {
  const { user } = useAuth();
  const mine = user ? coops.filter((c) => c.owner_id === user.id || ["admin", "collective_executive"].includes(user.role)) : [];
  return (
    <div className="space-y-4" data-testid="schedules-panel">
      {user && PRODUCER.includes(user.role) && mine.length > 0 && <ScheduleForm coops={mine} onCreated={onChange} />}
      {schedules.map((s) => (
        <div key={s.id} className="qs-panel p-5 grid lg:grid-cols-3 gap-5" data-testid={`schedule-${s.id}`}>
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500"><CalendarRange className="h-3.5 w-3.5" /> {s.season} · {s.method}</div>
            <h4 className="font-display font-bold text-xl mt-1">{s.product} · {s.planned_kg.toLocaleString()} kg</h4>
            <div className="text-xs text-slate-500">{s.coop_name} · {s.city} · water: {s.water_source}</div>
            <div className="text-[10px] font-mono text-slate-600 mt-2 truncate">anchor {s.anchor}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Collective workflow</div>
            <ol className="space-y-1.5 text-xs">
              {s.workflow.map((w) => <li key={w.step} className="flex gap-2"><span className="text-[#00FF7F] font-mono shrink-0">{w.when}</span><span className="text-slate-300">{w.step} <span className="text-slate-600">· {w.owner}</span></span></li>)}
            </ol>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Alternatives</div>
            <ul className="space-y-1.5 text-xs">
              {s.alternatives.map((a) => <li key={a.product + a.rationale} className="flex gap-2"><Sprout className="h-3.5 w-3.5 text-[#F59E0B] shrink-0 mt-0.5" /><span><b className="text-slate-200">{a.product}</b> <span className="text-slate-600">({a.futures_trend})</span> — <span className="text-slate-400">{a.rationale}</span></span></li>)}
            </ul>
          </div>
        </div>
      ))}
      {!schedules.length && <p className="text-sm text-slate-500">No production schedules yet.</p>}
    </div>
  );
};
